import { useQueryClient } from '@tanstack/react-query'

import { LotAssertionWrite } from '~/app/accounting/lot-assertion-writes'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'

// Invalidating the lots list only picks up the live assertion; corrected
// values land when the rebuild the server enqueued finishes, so the recompute
// query is invalidated too and the view starts following it.
export const useLotAssertionWrite = () => {
  const { serverPutRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()
  const { org } = useActiveOrg()
  const { closeGlobalModal } = useAuth()

  type WriteMessages = {
    successMessage: string
    successDescription: string
    errorMessage: string
  }

  const write = async ({
    path,
    params,
    successMessage,
    successDescription,
    errorMessage,
  }: WriteMessages & { path: string; params: object }) => {
    if (!org?.id) return

    const result = await serverPutRequest({
      path,
      params: { orgId: org.id, ...params },
      successMessage,
      successMessageOptions: { description: successDescription },
      errorMessage,
    })
    if (result) {
      ;[
        QUERY_KEYS.ORG_ACCOUNTING_LOTS,
        QUERY_KEYS.ORG_ACCOUNTING_LOT_ASSERTIONS,
        QUERY_KEYS.ORG_ACCOUNTING_LOT_RECOMPUTE,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
      closeGlobalModal()
    }
  }

  const writeAssertion = ({
    assertion,
    ...messages
  }: WriteMessages & { assertion: LotAssertionWrite }) =>
    write({
      path: '/api/org/accounting/lot-assertions',
      params: { assertion },
      ...messages,
    })

  // One atomic insert: writes that belong together (a seed and its
  // designation) land whole or not at all.
  const writeAssertionBundle = ({
    assertions,
    ...messages
  }: WriteMessages & { assertions: LotAssertionWrite[] }) =>
    write({
      path: '/api/org/accounting/lot-assertions/bulk',
      params: { assertions },
      ...messages,
    })

  return { writeAssertion, writeAssertionBundle, isProcessing }
}
